import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Compass, Users, Trophy, Gamepad2, Loader2, UserPlus, UserMinus, Activity, Clock, TrendingUp, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const TABS = [
  { id: "feed", label: "Aktivite", icon: Activity },
  { id: "users", label: "Kullanıcılar", icon: Users },
  { id: "leagues", label: "Ligler", icon: Trophy },
];

export default function Discover() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("feed");
  const [feed, setFeed] = useState([]);
  const [users, setUsers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState({});

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "feed") {
        const response = await api.getFeed();
        setFeed(response.data);
      } else if (activeTab === "users") {
        const response = await api.discoverUsers();
        setUsers(response.data);
        
        // Build following map
        if (user) {
          const map = {};
          for (const u of response.data) {
            try {
              const checkRes = await api.checkFollowing(u.user_id);
              map[u.user_id] = checkRes.data.is_following;
            } catch (e) {
              map[u.user_id] = false;
            }
          }
          setFollowingMap(map);
        }
      } else if (activeTab === "leagues") {
        const response = await api.discoverLeagues();
        setLeagues(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId) => {
    if (!user) {
      toast.error("Giriş yapmalısınız");
      return;
    }

    try {
      if (followingMap[userId]) {
        await api.unfollowUser(userId);
        setFollowingMap({ ...followingMap, [userId]: false });
        toast.success("Takipten çıkıldı");
      } else {
        await api.followUser(userId);
        setFollowingMap({ ...followingMap, [userId]: true });
        toast.success("Takip edildi");
      }
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Şimdi";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} dakika önce`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} saat önce`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} gün önce`;
    return date.toLocaleDateString("tr-TR");
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "league_created": return <Trophy className="w-4 h-4 text-[#C6A87C]" />;
      case "week_added": return <Gamepad2 className="w-4 h-4 text-[#4DFFFF]" />;
      case "badge_earned": return <Star className="w-4 h-4 text-[#C6A87C]" />;
      case "followed": return <UserPlus className="w-4 h-4 text-green-400" />;
      default: return <Activity className="w-4 h-4 text-[#64748B]" />;
    }
  };

  const getActivityMessage = (activity) => {
    switch (activity.activity_type) {
      case "league_created":
        return (
          <>
            <span className="text-white font-medium">{activity.user?.name}</span>
            <span className="text-[#B1C1C1]"> yeni bir lig oluşturdu: </span>
            <Link to={`/league/${activity.league_id}`} className="text-[#4DFFFF] hover:underline">
              {activity.league_name}
            </Link>
          </>
        );
      case "week_added":
        return (
          <>
            <span className="text-white font-medium">{activity.user?.name}</span>
            <span className="text-[#B1C1C1]"> bir hafta ekledi: </span>
            <Link to={`/league/${activity.league_id}`} className="text-[#4DFFFF] hover:underline">
              {activity.league_name} - Hafta {activity.week_number}
            </Link>
          </>
        );
      case "badge_earned":
        return (
          <>
            <span className="text-white font-medium">{activity.user?.name}</span>
            <span className="text-[#B1C1C1]"> yeni bir rozet kazandı: </span>
            <span className="text-[#C6A87C]">{activity.badge_name}</span>
          </>
        );
      case "followed":
        return (
          <>
            <span className="text-white font-medium">{activity.user?.name}</span>
            <span className="text-[#B1C1C1]"> takip etmeye başladı: </span>
            <Link to={`/profile/${activity.target_username}`} className="text-[#4DFFFF] hover:underline">
              @{activity.target_username}
            </Link>
          </>
        );
      default:
        return (
          <>
            <span className="text-white font-medium">{activity.user?.name}</span>
            <span className="text-[#B1C1C1]"> bir aktivite gerçekleştirdi</span>
          </>
        );
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="discover-page">
      <div className="flex items-center gap-3 mb-6">
        <Compass className="w-7 h-7 text-[#4DFFFF]" />
        <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Keşfet
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-['Barlow_Condensed'] uppercase tracking-wider text-sm transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-[#4DFFFF] text-[#050E2E] font-bold"
                  : "bg-white/5 text-[#B1C1C1] hover:bg-white/10"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
        </div>
      ) : (
        <>
          {/* Activity Feed */}
          {activeTab === "feed" && (
            <div className="space-y-4" data-testid="activity-feed">
              {feed.length > 0 ? (
                feed.map((activity) => (
                  <div key={activity.activity_id} className="glass-card p-4">
                    <div className="flex gap-3">
                      <Link to={`/profile/${activity.user?.username}`}>
                        <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center flex-shrink-0">
                          {activity.user?.picture ? (
                            <img src={activity.user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-sm font-bold text-[#C6A87C]">
                              {activity.user?.name?.charAt(0)}
                            </span>
                          )}
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getActivityIcon(activity.activity_type)}
                          <span className="text-xs text-[#64748B]">{formatTime(activity.created_at)}</span>
                        </div>
                        <p className="text-sm leading-relaxed">
                          {getActivityMessage(activity)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="glass-card p-12 text-center">
                  <Activity className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">Aktivite Akışı Boş</h3>
                  <p className="text-[#B1C1C1] mb-4">
                    Kullanıcıları takip et ve aktivitelerini burada gör
                  </p>
                  <Button
                    onClick={() => setActiveTab("users")}
                    className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90"
                  >
                    Kullanıcıları Keşfet
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Users */}
          {activeTab === "users" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="users-list">
              {users.length > 0 ? (
                users.filter(u => u.user_id !== user?.user_id).map((u) => (
                  <div key={u.user_id} className="glass-card p-4">
                    <div className="flex items-center justify-between">
                      <Link to={`/profile/${u.username}`} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-[#C6A87C]">{u.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-sm text-[#64748B]">@{u.username}</p>
                        </div>
                      </Link>
                      {user && u.user_id !== user.user_id && (
                        <Button
                          variant={followingMap[u.user_id] ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleFollow(u.user_id)}
                          className={followingMap[u.user_id] 
                            ? "border-white/20 text-white hover:bg-white/10" 
                            : "bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90"
                          }
                          data-testid={`follow-btn-${u.user_id}`}
                        >
                          {followingMap[u.user_id] ? (
                            <>
                              <UserMinus className="w-4 h-4 mr-1" />
                              Takibi Bırak
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-1" />
                              Takip Et
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-4 mt-3 text-xs text-[#64748B]">
                      <span>{u.followers_count || 0} takipçi</span>
                      <span>{u.leagues_count || 0} lig</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 glass-card p-12 text-center">
                  <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">Henüz Kullanıcı Yok</h3>
                  <p className="text-[#B1C1C1]">İlk kullanıcılar burada görünecek</p>
                </div>
              )}
            </div>
          )}

          {/* Leagues */}
          {activeTab === "leagues" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leagues-list">
              {leagues.length > 0 ? (
                leagues.map((league) => (
                  <Link key={league.league_id} to={`/league/${league.league_id}`} className="glass-card p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${league.color || '#C6A87C'}20` }}>
                        <Trophy className="w-5 h-5" style={{ color: league.color || '#C6A87C' }} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{league.name}</p>
                        <p className="text-xs text-[#64748B]">{league.game_name || league.game_id}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs text-[#64748B]">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {league.players?.length || 0} oyuncu
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {league.weeks?.length || 0} hafta
                      </span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-2 glass-card p-12 text-center">
                  <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">Henüz Lig Yok</h3>
                  <p className="text-[#B1C1C1] mb-4">Herkese açık ligler burada görünecek</p>
                  <Link to="/create-league">
                    <Button className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                      İlk Ligi Oluştur
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
